import styled from '@emotion/styled';

import {
    BodyTextMedium,
    BodyTextSmall,
    HeadingText10,
    HeadingText20,
    HeadingText30,
    HeadingText50,
    Modal as DsModal,
    pxToRem,
} from '@modernhealth/vital-components';

import { colors } from '@modernhealth/vital-foundation';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { userService } from 'src/config/rootService';
import { Dispatch } from 'src/config/rootStore';
import { CrisisProviderNode } from 'src/generated/webapp_gql';
import { CrisisModalTestID } from 'src/modules/benefits/models/testsIds';
import { getSortedCountries } from 'src/modules/countries/selectors/countrySelectors';
import { getUserInformation } from 'src/modules/user/selectors/userSelectors';
import { CountrySelect } from 'src/shared/components/CountrySelect';
import { hexToRgba } from 'src/shared/helpers/hexToRgba';
import { useLocalizedVersion } from 'src/shared/hooks/useLocalizedVersion';
import helpIcon from 'src/shared/svg/icon-help.svg';
import phoneIcon from 'src/shared/svg/icon-phone.svg';

interface StyledIconProps {
    iconUrl: string;
}

const StyledIcon = styled('i')<StyledIconProps>(({ iconUrl }) => ({
    backgroundImage: `url('${iconUrl}')`,
    width: '1.75rem',
    height: '1.75rem',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'contain',
    backgroundPosition: 'center',
}));

const ContentWrapper = styled.div<{ isVisible: boolean }>(
    props => (props.isVisible ? {} : { display: 'none' }),
    {
        maxWidth: 700,
        padding: 10,
        overflowY: 'auto',
        maxHeight: pxToRem(600),
    },
);

const NumberContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    marginTop: 25,
});

const LabelContainer = styled.div({
    display: 'flex',
    flexFlow: 'row',
    alignItems: 'center',
    marginBottom: 16,
});

const InfoContainer = styled.div({
    width: '100%',
    borderRadius: 5,
    padding: '15px 12px',
    backgroundColor: hexToRgba(colors.gray05, 0.5),
    color: colors.gray65,
    p: {
        lineHeight: 1.8,
    },
});

const InputContainer = styled.div({
    marginTop: pxToRem(20),
});

interface Props {
    visible: boolean;
    onClose: () => void;
    source?: string;
}

const urls = {
    internationalSuicideHotlines: 'http://www.suicide.org/international-suicide-hotlines.html',
    internationalHelplines: 'https://togetherweare-strong.tumblr.com/helpline',
    volunteerPhone: 'https://www.befrienders.org/help-and-support',
};

export const CrisisModal: React.FC<Props> = ({ onClose, visible, source }) => {
    const { t } = useTranslation();
    const user = useSelector(getUserInformation);
    const dispatch = useDispatch<Dispatch>();
    const countries = useSelector(getSortedCountries);
    const { showCrisisResourceAhpCopy, showSuicideAndCrisisLifeline } = useFlags();
    const [localizedVersion] = useLocalizedVersion();

    const AHP_MODAL_COPY =
        'If you are experiencing a mental health emergency, please go to the nearest emergency room or contact a local emergency response line.';

    const [country, setCountry] = useState(user.country);

    const companyCrisisProviders = user.crisisProviderCompanyAssignments;
    const countryCrisisProviders = user.crisisProviderCountryAssignments;
    const showAdditionalProvider = companyCrisisProviders?.length > 0;

    const countryPoliceContact = {};
    const countriesSuicideHotlineContact = {};
    const countriesSuicideAndCrisisLifeline = {};

    countries.map(({ name, policePhoneNumber, suicideHotlineNumber, suicideAndCrisisLifeline }) => {
        countryPoliceContact[name] = policePhoneNumber;
        countriesSuicideHotlineContact[name] = suicideHotlineNumber;
        countriesSuicideAndCrisisLifeline[name] = suicideAndCrisisLifeline;
    });

    const policeContact = (selectedCountry: string) => {
        if (!countryPoliceContact[selectedCountry]) {
            return t('shared:crisis_modal.police_contact.description');
        }

        return countryPoliceContact[selectedCountry || 'United States'];
    };

    const suicideAndCrisisLifeline = (selectedCountry: string) => {
        if (!countriesSuicideAndCrisisLifeline[selectedCountry]) {
            return false;
        }

        return countriesSuicideAndCrisisLifeline[selectedCountry || 'United States'];
    };

    const suicideHotline = (selectedCountry: string) => {
        if (!countriesSuicideHotlineContact[selectedCountry]) {
            return t('shared:crisis_modal.suicide_hotline.description');
        }

        return t(countriesSuicideHotlineContact[selectedCountry || 'United States']);
    };

    const companyCrisisProvidersDict = {};

    for (const i in companyCrisisProviders) {
        if (companyCrisisProviders.hasOwnProperty(i)) {
            const provider = companyCrisisProviders[i];
            // Dictionary to store phone numbers by country
            const providerPhoneNumberByCountry = {};
            for (const j in countryCrisisProviders) {
                if (countryCrisisProviders.hasOwnProperty(j)) {
                    const providerCountry = countryCrisisProviders[j];
                    if (
                        providerCountry.crisisProvider.name === provider.crisisProvider.name &&
                        providerCountry.phoneNumber
                    ) {
                        providerPhoneNumberByCountry[providerCountry.country.name] =
                            providerCountry.phoneNumber;
                    }
                }
            }
            companyCrisisProvidersDict[provider.crisisProvider.name] = providerPhoneNumberByCountry;
        }
    }

    const getPhoneNumberByCountryAndProvider = (
        provider: CrisisProviderNode,
        selectedCountry: string,
    ) => {
        return (
            companyCrisisProvidersDict[provider.name][selectedCountry] ??
            provider.defaultPhoneNumber
        );
    };

    useEffect(() => {
        if (visible && source) {
            userService.submitCrisisResourceEvent(source);
        }
    }, [visible, source]);

    return (
        <DsModal maxWidth={772} isVisible={visible} onCloseButtonClick={onClose}>
            <ContentWrapper data-testid={CrisisModalTestID.Modal} isVisible={visible}>
                <HeadingText50 marginBottom={16}>{t(`shared:crisis_modal.header`)}</HeadingText50>
                <BodyTextMedium marginBottom={36}>
                    {showCrisisResourceAhpCopy && !localizedVersion
                        ? AHP_MODAL_COPY
                        : t(`shared:crisis_modal.description`)}
                </BodyTextMedium>
                <InputContainer>
                    <CountrySelect
                        id="crisisFormCountryInput"
                        defaultValue={user.country}
                        onChange={country => {
                            if (!country) {
                                return;
                            }

                            setCountry(country.value);

                            dispatch.user.updateUserInfo({
                                country: country.value,
                            });
                        }}
                    />
                </InputContainer>

                {showAdditionalProvider
                    ? companyCrisisProviders.map(
                          crisisProvider =>
                              getPhoneNumberByCountryAndProvider(
                                  crisisProvider.crisisProvider,
                                  country,
                              ) && (
                                  <NumberContainer key={crisisProvider.id}>
                                      <LabelContainer>
                                          <StyledIcon iconUrl={helpIcon} />{' '}
                                          <HeadingText20 marginLeft={16}>
                                              {crisisProvider.crisisProvider.name}:
                                          </HeadingText20>
                                      </LabelContainer>
                                      <InfoContainer>
                                          <BodyTextSmall>
                                              {getPhoneNumberByCountryAndProvider(
                                                  crisisProvider.crisisProvider,
                                                  country,
                                              )}
                                          </BodyTextSmall>
                                      </InfoContainer>
                                  </NumberContainer>
                              ),
                      )
                    : null}

                {showSuicideAndCrisisLifeline &&
                    suicideAndCrisisLifeline(country) &&
                    country === 'United States' && (
                        <NumberContainer>
                            <LabelContainer>
                                <StyledIcon iconUrl={phoneIcon} />
                                <HeadingText20 marginLeft={16}>
                                    {t('shared:crisis_modal.suicide_crisis_lifeline.label')}
                                </HeadingText20>
                            </LabelContainer>
                            <InfoContainer>
                                <BodyTextSmall>{suicideAndCrisisLifeline(country)}</BodyTextSmall>
                            </InfoContainer>
                        </NumberContainer>
                    )}

                {suicideHotline(country) && country !== 'United States' && (
                    <NumberContainer>
                        <LabelContainer>
                            <StyledIcon iconUrl={phoneIcon} />
                            <HeadingText20 marginLeft={16}>
                                {t('shared:crisis_modal.suicide_hotline.label')}:
                            </HeadingText20>
                        </LabelContainer>
                        <InfoContainer>
                            <BodyTextSmall>{suicideHotline(country)}</BodyTextSmall>
                        </InfoContainer>
                    </NumberContainer>
                )}

                <NumberContainer>
                    <LabelContainer>
                        <StyledIcon iconUrl={helpIcon} />
                        <HeadingText20 marginLeft={16}>
                            {t('shared:crisis_modal.police_contact.label')}:
                        </HeadingText20>
                    </LabelContainer>
                    <InfoContainer>
                        <BodyTextSmall>{policeContact(country)}</BodyTextSmall>
                    </InfoContainer>
                </NumberContainer>

                <HeadingText30 marginTop={32}>
                    {t('shared:crisis_modal.international.label')}
                </HeadingText30>
                <HeadingText10 underline fontColor="blue55" marginTop={24}>
                    <a
                        data-testid={CrisisModalTestID.InternationalSuicideHotlines}
                        target="_blank"
                        href={urls.internationalSuicideHotlines}
                        rel="noopener noreferrer"
                    >
                        {t('shared:crisis_modal.international_suicide_hotlines.title')}
                    </a>
                </HeadingText10>
                <HeadingText10 underline fontColor="blue55" marginTop={20}>
                    <a
                        target="_blank"
                        data-testid={CrisisModalTestID.InternationalHelplines}
                        href={urls.internationalHelplines}
                        rel="noopener noreferrer"
                    >
                        {t('shared:crisis_modal.international_helplines.title')}
                    </a>
                </HeadingText10>
                <HeadingText10 underline fontColor="blue55" marginTop={20}>
                    <a
                        target="_blank"
                        data-testid={CrisisModalTestID.VolunteerPhone}
                        href={urls.volunteerPhone}
                        rel="noopener noreferrer"
                    >
                        {t('shared:crisis_modal.volunteer_phone.title')}
                    </a>
                </HeadingText10>
            </ContentWrapper>
        </DsModal>
    );
};
